import { useDebouncedValue } from '@openx/utils/hooks/useDebouncedValue.ts';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { CreativeThumbnail } from '../../components/preview/CreativeThumbnail.tsx';
import { PREVIEW_DEBOUNCE_MS } from '../../components/preview/constants.ts';
import type { BannerCreativeFormData } from '../validation/bannerCreative.ts';

export const BannerThumbnail = () => {
  const {
    control,
    setValue,
    formState: { dirtyFields },
  } = useFormContext<BannerCreativeFormData>();

  const observableMarkupPayload = useWatch({ control, name: 'markup_payload' });

  const debouncedMarkup = useDebouncedValue(observableMarkupPayload, PREVIEW_DEBOUNCE_MS);

  const [detectedDimensions, setDetectedDimensions] = useState<{ width: number; height: number } | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: debouncedMarkup is intentionally used as a trigger to reset dimensions on markup change
  useEffect(() => {
    setDetectedDimensions(null);
  }, [debouncedMarkup]);

  const handleDimensionsDetected = useCallback(
    (dimensions: { width: number; height: number }) => {
      setDetectedDimensions(dimensions);

      if (!dirtyFields.markup_payload) return;
      setValue('width', String(dimensions.width), { shouldDirty: true, shouldValidate: true });
      setValue('height', String(dimensions.height), { shouldDirty: true, shouldValidate: true });
    },
    [setValue, dirtyFields.markup_payload]
  );

  return (
    <CreativeThumbnail
      html={debouncedMarkup}
      width={detectedDimensions?.width}
      height={detectedDimensions?.height}
      onDimensionsDetected={handleDimensionsDetected}
    />
  );
};
